/**
 * Ported from the lekterion codebase (`src/components/language-provider.tsx`) @ `d942f2f`.
 * Bindery is canonical for this module from 2026-09-06; reconcile source-side
 * drift toward this copy.
 */
import * as React from "react";

import { asTargetLang, DEFAULT_TARGET_LANG, type TargetLang } from "@/lib/i18n";

/**
 * Global translation-target preference (§5+). The reader's chosen language flows
 * into every gloss/translate call and the frozen cache key, so each language
 * caches on its own row. Persisted to localStorage (mirrors ThemeProvider /
 * AutoplayProvider) and synced across tabs. Defaults to English.
 */

interface LanguageState {
  targetLang: TargetLang;
  setTargetLang: (next: TargetLang) => void;
}

const LanguageContext = React.createContext<LanguageState | undefined>(
  undefined,
);

const STORAGE_KEY = "lekterion:targetLang";

function readStored(): TargetLang {
  try {
    return (
      asTargetLang(localStorage.getItem(STORAGE_KEY)) ?? DEFAULT_TARGET_LANG
    );
  } catch {
    return DEFAULT_TARGET_LANG;
  }
}

export function LanguageProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const [targetLang, setTargetLangState] =
    React.useState<TargetLang>(readStored);

  const setTargetLang = React.useCallback((next: TargetLang) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage unavailable — preference stays in-session */
    }
    setTargetLangState(next);
  }, []);

  // Mirror a change made in another tab.
  React.useEffect(() => {
    const onStorage = (e: StorageEvent): void => {
      if (e.storageArea !== localStorage || e.key !== STORAGE_KEY) return;
      setTargetLangState(asTargetLang(e.newValue) ?? DEFAULT_TARGET_LANG);
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const value = React.useMemo(
    () => ({ targetLang, setTargetLang }),
    [targetLang, setTargetLang],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTargetLang(): LanguageState {
  const ctx = React.useContext(LanguageContext);
  if (ctx === undefined) {
    throw new Error("useTargetLang must be used within a LanguageProvider");
  }
  return ctx;
}
